import React from 'react';
import classNames from 'classnames/bind';
import { DatesRangeValue } from '@mantine/dates';
import { MultiValue } from 'react-select';
import { useDebouncedValue } from '@tanstack/react-pacer';

import { Camera } from 'types/camera';
import { Stream } from 'types/stream';
import { Gateway } from 'types/gateway';
import { TrackableItem, TrackableList } from 'types/trackables';

import { useCursorPagination } from 'components/Pagination/hooks/useCursorPagination';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import {
  FilesSearchEntity,
  ImageSearchType,
  UseFilesSearchParams,
  useFilesSearch,
} from 'files/hooks/useFilesSearch';
import { useFilesCursorWithSearchMetadata } from 'files/hooks/useFilesWithSearchMetadata';
import { transformSearchQuery } from 'files/services/transformSearchQuery';

import {
  DATE_RANGE_PRESETS,
  RelativeDateRange,
} from 'components/DateRangePickerInput/time_range';

import * as PageLayout from 'components/PageLayout';
import { Button } from 'components/Button/Button';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import {
  DateRange,
  DateTimeRangeInput,
} from 'components/DateTimeRangeInput/DateTimeRangeInput';
import { FilesSearchConfidenceSlider } from 'files/components/FilesSearchConfidenceSlider/FilesSearchConfidenceSlider';
import { FilesSearchEntitiesInput } from 'files/components/FilesSearchEntitiesInput/FilesSearchEntitiesInput';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Icon } from 'components/Icon/IconDeprecated';
import { ImageSearchDialog } from 'files/components/ImageSearchDialog/ImageSearchDialog';
import { OptionalTooltip } from 'components/Tooltip/OptionalTooltip';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { Radio, RadioOption } from 'components/Radio/Radio';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';
import { TrackableItemSelect } from 'trackables/components/TrackableItemSelect/TrackableItemSelect';
import { TrackableListSelect } from 'trackables/components/TrackableListSelect/TrackableListSelect';

import { FilesBulkManager } from './BulkManager';
import { FilesSearchProvider } from './Provider';
import { FilesSearchResults } from './Results';
import { FilesSearchUpgradeNotice } from './UpgradeNotice';
import styles from './Search.module.scss';

const c = classNames.bind(styles);

const DEFAULT_DATE_PRESET: RelativeDateRange = 'THIS_WEEK';
const DEFAULT_SEARCH_ENTITIES: FilesSearchEntity[] = [
  'events',
  'objects',
  'texts',
  'trackables',
  'vectors',
];

// Internal filter state — page is tracked separately for the search (offset) path
type SearchFilters = UseFilesSearchParams & {
  search_entities: FilesSearchEntity[];
  trackable_list_ids?: string[];
  trackable_item_ids?: string[];
};

export function FilesSearch() {
  const { data: currentPlan } = useCurrentPlan();

  const [filters, setFilters] = React.useState<SearchFilters>(() => {
    const [since, until] = DATE_RANGE_PRESETS[DEFAULT_DATE_PRESET]();

    return {
      created_ts_since: since.toISOString(),
      created_ts_until: until.toISOString(),
      search_entities: DEFAULT_SEARCH_ENTITIES,
      confidence: 0.5,
      sort_by: 'created_at',
      // Custom limit to allow for neat grids on common screen sizes
      limit: 48,
    };
  });

  const cursor = useCursorPagination({ limit: 48 });
  const [searchPage, setSearchPage] = React.useState(1);
  const [dateRange, setDateRange] = React.useState<DateRange>(DEFAULT_DATE_PRESET);
  const [isImageSearchOpen, setIsImageSearchOpen] = React.useState(false);
  const [imageSearchEmbedding, setImageSearchEmbedding] = React.useState<
    number[] | undefined
  >();
  const [imageSearchType, setImageSearchType] = React.useState<
    ImageSearchType | undefined
  >();
  const [imageSearchPreviewUrl, setImageSearchPreviewUrl] = React.useState<
    string | undefined
  >();
  const [selectedTrackableLists, setSelectedTrackableLists] = React.useState<
    TrackableList[]
  >([]);
  const [selectedTrackableItems, setSelectedTrackableItems] = React.useState<
    TrackableItem[]
  >([]);
  const hasSelectedTrackableLists = selectedTrackableLists.length > 0;
  const [debouncedFilters] = useDebouncedValue(filters, { wait: 200 });
  const [debouncedImageSearchEmbedding] = useDebouncedValue(
    imageSearchEmbedding,
    { wait: 200 }
  );
  const [debouncedImageSearchType] = useDebouncedValue(imageSearchType, {
    wait: 200,
  });
  const filesFilters = React.useMemo(() => {
    const { trackable_list_ids, trackable_item_ids, ...rest } =
      debouncedFilters;
    return rest;
  }, [debouncedFilters]);

  const combinedQuery = React.useMemo(() => {
    const segments: string[] = [];

    if (debouncedFilters.query?.trim()) {
      segments.push(transformSearchQuery(debouncedFilters.query));
    }

    const trackableItemIds = debouncedFilters.trackable_item_ids ?? [];
    const trackableListIds = debouncedFilters.trackable_list_ids ?? [];

    if (trackableItemIds.length) {
      segments.push(trackableItemIds.join(' '));
    } else if (trackableListIds.length) {
      segments.push(trackableListIds.join(' '));
    }

    return segments.join(' ').trim();
  }, [debouncedFilters]);

  const searchParams = React.useMemo(() => {
    const { trackable_list_ids, trackable_item_ids, ...rest } =
      debouncedFilters;
    return {
      ...rest,
      query: combinedQuery || undefined,
    };
  }, [combinedQuery, debouncedFilters]);

  const isImageSearchActive = Boolean(
    imageSearchEmbedding?.length && imageSearchType
  );
  const isImageSearching = Boolean(
    debouncedImageSearchEmbedding?.length && debouncedImageSearchType
  );
  const isSearching = Boolean(searchParams.query) || isImageSearching;

  const filesSearchQueryResult = useFilesSearch({
    params: {
      ...searchParams,
      page: searchPage,
      ...(isImageSearching
        ? {
            query: undefined,
            type: debouncedImageSearchType,
            embedding: debouncedImageSearchEmbedding,
            search_entities: undefined,
          }
        : {}),
    },
    keepPreviousData: true,
    enabled: isSearching && currentPlan?.can_search,
  });

  const filesQueryResult = useFilesCursorWithSearchMetadata({
    params: {
      ...filesFilters,
      ...cursor.cursorParams,
      with_thumbnail: true,
      include_snapshots: false,
      include_search_metadata: currentPlan?.can_search,
      is_recording: true,
    },
    keepPreviousData: true,
    enabled: !isSearching,
  });

  function resetPagination() {
    cursor.reset();
    setSearchPage(1);
  }

  function handleApplySearchText(value?: string) {
    clearImageSearch();
    resetPagination();
    setFilters(({ query, ...previousFilters }) => {
      return value
        ? { ...previousFilters, query: value, sort_by: 'relevance' }
        : { ...previousFilters, sort_by: 'created_at' };
    });
  }

  function handleImageSearchSelect(embedding: number[], type: ImageSearchType, sourceFile: File) {
    setImageSearchEmbedding(embedding);
    setImageSearchType(type);
    if (imageSearchPreviewUrl) URL.revokeObjectURL(imageSearchPreviewUrl);
    setImageSearchPreviewUrl(URL.createObjectURL(sourceFile));
    setSelectedTrackableLists([]);
    setSelectedTrackableItems([]);
    resetPagination();
    // Faces match at lower similarity than full person crops, so default
    // each search kind to a sensible starting relevance threshold.
    const defaultConfidence = type === 'face_reid' ? 0.3 : 0.8;
    setFilters(({ query, ...previousFilters }) => ({
      ...previousFilters,
      trackable_list_ids: undefined,
      trackable_item_ids: undefined,
      search_entities: DEFAULT_SEARCH_ENTITIES,
      sort_by: 'relevance',
      confidence: defaultConfidence,
    }));
  }

  function clearImageSearch() {
    setImageSearchEmbedding(undefined);
    setImageSearchType(undefined);
    if (imageSearchPreviewUrl) URL.revokeObjectURL(imageSearchPreviewUrl);
    setImageSearchPreviewUrl(undefined);
  }

  function handleDateRangeChange(
    value?: DatesRangeValue,
    preset?: string
  ) {
    if (!value) return;

    const [since, until] = value;
    if (!since || !until) return;

    setDateRange(
      preset && preset !== 'CUSTOM'
        ? (preset as RelativeDateRange)
        : [since, until]
    );
    resetPagination();
    setFilters((previousFilters) => ({
      ...previousFilters,
      created_ts_since: since.toISOString(),
      created_ts_until: until.toISOString(),
    }));
  }

  function handleCamerasChange(cameras: MultiValue<Camera>) {
    resetPagination();
    setFilters((previousFilters) => ({
      ...previousFilters,
      camera_ids: cameras.map(({ id }) => id),
    }));
  }

  function handleStreamsChange(streams: MultiValue<Stream>) {
    resetPagination();
    setFilters((previousFilters) => ({
      ...previousFilters,
      stream_ids: streams.map(({ id }) => id),
    }));
  }

  function handleGatewaysChange(gateways: MultiValue<Gateway>) {
    resetPagination();
    setFilters((previousFilters) => ({
      ...previousFilters,
      gateway_ids: gateways.map(({ id }) => id),
    }));
  }

  function handleTrackableListsChange(lists: MultiValue<TrackableList>) {
    const selections = Array.isArray(lists) ? [...lists] : [];
    const selectedIds = selections.map(({ id }) => id);
    const hasSelections = selectedIds.length > 0;
    const filteredItems = selectedTrackableItems.filter((item) =>
      selectedIds.includes(item.trackable_list_id)
    );

    setSelectedTrackableLists(selections);
    setSelectedTrackableItems(filteredItems);
    resetPagination();

    setFilters((previousFilters) => ({
      ...previousFilters,
      trackable_list_ids: hasSelections ? selectedIds : undefined,
      trackable_item_ids: filteredItems.length
        ? filteredItems.map(({ id }) => id)
        : undefined,
      search_entities: hasSelections
        ? (['trackables'] as FilesSearchEntity[])
        : DEFAULT_SEARCH_ENTITIES,
      sort_by: hasSelections ? 'created_at' : previousFilters.sort_by,
    }));
  }

  function handleTrackableItemsChange(items: MultiValue<TrackableItem>) {
    const selections = Array.isArray(items) ? [...items] : [];

    setSelectedTrackableItems(selections);
    resetPagination();

    setFilters((previousFilters) => {
      let searchEntities: FilesSearchEntity[];

      if (selections.length) {
        searchEntities = ['trackables'];
      } else if (previousFilters.trackable_list_ids?.length) {
        searchEntities = previousFilters.search_entities;
      } else {
        searchEntities = DEFAULT_SEARCH_ENTITIES;
      }

      return {
        ...previousFilters,
        trackable_item_ids: selections.length
          ? selections.map(({ id }) => id)
          : undefined,
        search_entities: searchEntities,
      };
    });
  }

  function handleSearchEntitiesChange(searchEntities: FilesSearchEntity[]) {
    const includesTrackables = searchEntities.includes('trackables');
    resetPagination();

    setFilters(({ confidence, ...previousFilters }) => {
      const nextFilters = {
        ...previousFilters,
        search_entities: searchEntities,
        // Remove confidence if vectors filter has been removed
        confidence: searchEntities.includes('vectors') ? confidence : undefined,
      };

      if (!includesTrackables) {
        nextFilters.trackable_list_ids = undefined;
        nextFilters.trackable_item_ids = undefined;
      }

      return nextFilters;
    });

    if (!includesTrackables) {
      setSelectedTrackableLists([]);
      setSelectedTrackableItems([]);
    }
  }

  function handleConfidenceChange(confidence: number) {
    resetPagination();
    setFilters((previousFilters) => ({
      ...previousFilters,
      confidence,
    }));
  }

  function handleSortChange(sort: string) {
    if (sort === 'created_at' || sort === 'relevance') {
      resetPagination();
      setFilters((previousFilters) => ({
        ...previousFilters,
        sort_by: sort,
      }));
    }
  }

  function handleSearchPageChange(page: number) {
    setSearchPage(page);
  }

  const cursorPaginationProps = cursor.getProps(
    filesQueryResult.data,
    filesQueryResult.isFetching
  );

  function getAllIdsOnPage() {
    if (isSearching && currentPlan?.can_search) {
      return filesSearchQueryResult.data?.data.map(({ file_id }) => file_id);
    }

    return filesQueryResult.data?.data.map(({ id }) => id);
  }

  const searchEntities = filters.search_entities;
  const isConfidenceSliderDisabled =
    !isImageSearchActive &&
    !(searchEntities.length && searchEntities.includes('vectors'));

  const canSearch = currentPlan && currentPlan.can_search;
  const shouldShowUpgradeNotice = !canSearch && isSearching;

  const queryResult =
    canSearch && isSearching ? filesSearchQueryResult : filesQueryResult;

  return (
    <PageLayout.Root className={c('wrap')}>
      <OptionalTooltip
        showTooltip={isImageSearchActive}
        content="Text search is disabled while Appearance search is active"
      >
        <QuerySearch
          value={filters.query ?? ''}
          orientation="vertical"
          entityName="file"
          onValueChange={handleApplySearchText}
          disabled={isImageSearchActive}
          searchLabel='Search recordings... ex. car, object.attribute, *ABC* (wildcards), "exact" (quotes), person wearing white shirt (Similarity)'
          inputActions={
            isImageSearchActive ? (
              <Tooltip
                content={
                  imageSearchPreviewUrl ? (
                    <img
                      src={imageSearchPreviewUrl}
                      alt="Search image"
                      className="max-h-48 max-w-48 rounded"
                    />
                  ) : (
                    'Clear appearance search'
                  )
                }
                side="bottom"
              >
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => {
                    clearImageSearch();
                    resetPagination();
                    setFilters((prev) => ({
                      ...prev,
                      sort_by: 'created_at',
                    }));
                  }}
                >
                  Clear appearance search
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="small"
                onClick={() => setIsImageSearchOpen(true)}
              >
                <Icon name="image" className="!mr-1" />
                <span>Appearance search</span>
              </Button>
            )
          }
          filters={
            <div className={c('filters')}>
              <div className={c('filters-group')}>
                <DateTimeRangeInput
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  disabled={shouldShowUpgradeNotice}
                />

                <CameraSelect
                  size="small"
                  onChange={handleCamerasChange}
                  isMulti
                  isDisabled={shouldShowUpgradeNotice}
                />

                <StreamSelect
                  size="small"
                  onChange={handleStreamsChange}
                  queryFilters={{
                    sources: ['camera_stream', 'uri_stream'],
                    stream_types: ['file', 'rtsp'],
                  }}
                  isMulti
                  isDisabled={shouldShowUpgradeNotice}
                />

                <GatewaySelect
                  size="small"
                  onChange={handleGatewaysChange}
                  isMulti
                  isDisabled={shouldShowUpgradeNotice}
                />

                <TrackableListSelect
                  size="small"
                  value={selectedTrackableLists}
                  onChange={handleTrackableListsChange}
                  isMulti
                  isDisabled={shouldShowUpgradeNotice}
                />

                <TrackableItemSelect<true>
                  size="small"
                  value={selectedTrackableItems}
                  onChange={handleTrackableItemsChange}
                  trackableLists={selectedTrackableLists}
                  isMulti
                  isClearable
                  isDisabled={
                    shouldShowUpgradeNotice || !hasSelectedTrackableLists
                  }
                />
              </div>

              <OptionalTooltip
                showTooltip={isImageSearchActive}
                content="Text search is disabled while Appearance search is active"
              >
                <div className={c('filters-group')}>
                  <strong>Search by</strong>
                  <FilesSearchEntitiesInput
                    id="files-search-entities-input"
                    defaultValue={DEFAULT_SEARCH_ENTITIES}
                    onChange={handleSearchEntitiesChange}
                    disabled={
                      isImageSearchActive ||
                      !isSearching ||
                      shouldShowUpgradeNotice
                    }
                    value={filters.search_entities}
                  />
                </div>
              </OptionalTooltip>

              <OptionalTooltip
                className={c('filters-confidence-wrap')}
                showTooltip={isConfidenceSliderDisabled}
                content="Enable scenes search to set confidence"
              >
                <FilesSearchConfidenceSlider
                  value={filters.confidence ?? 0.5}
                  onChangeEnd={handleConfidenceChange}
                  disabled={
                    isConfidenceSliderDisabled ||
                    !isSearching ||
                    shouldShowUpgradeNotice
                  }
                />
              </OptionalTooltip>

              <div className={c('filters-group')}>
                <label htmlFor="search_sort">
                  <strong>Sort by</strong>
                </label>
                <Radio
                  id="search_sort"
                  value={filters.sort_by}
                  onChange={(value) =>
                    value &&
                    typeof value === 'string' &&
                    handleSortChange(value)
                  }
                  disabled={!isSearching || shouldShowUpgradeNotice}
                >
                  <RadioOption value="relevance">Relevance</RadioOption>
                  <RadioOption value="created_at">Date</RadioOption>
                </Radio>
              </div>
            </div>
          }
        />
      </OptionalTooltip>
      <FilesSearchProvider>
        <PageLayout.Content>
          {shouldShowUpgradeNotice ? (
            <div className={c('info')}>
              <FilesSearchUpgradeNotice
                onCancel={() => {
                  resetPagination();
                  setFilters(({ ...previousFilters }) => ({
                    ...previousFilters,
                    query: '',
                    sort_by: 'created_at',
                  }));
                }}
              />
            </div>
          ) : (
            <FilesSearchResults dateRange={dateRange} queryResult={queryResult} />
          )}
        </PageLayout.Content>

        {queryResult.data && (
          <FilesBulkManager
            {...(isSearching
              ? {
                  page: searchPage,
                  hasNextPage:
                    searchPage <
                    (filesSearchQueryResult.data?.total_pages ?? 1),
                  hasPreviousPage: searchPage > 1,
                  onNextPage: () => handleSearchPageChange(searchPage + 1),
                  onPreviousPage: () =>
                    handleSearchPageChange(Math.max(1, searchPage - 1)),
                  disabled: queryResult.isFetching,
                }
              : cursorPaginationProps)}
            onSelectAllOnPage={getAllIdsOnPage}
          />
        )}
      </FilesSearchProvider>

      {isImageSearchOpen && (
        <ImageSearchDialog
          open={isImageSearchOpen}
          onClose={() => setIsImageSearchOpen(false)}
          onSelect={handleImageSearchSelect}
        />
      )}
    </PageLayout.Root>
  );
}
